import { Card, Col, Row, Statistic, Typography, theme as antTheme } from 'antd';
import type { InvoiceSummary } from '../../api/types';
import { MoneyText } from '../../lib/format';

const { Text } = Typography;

/** RO singular/plural for the count nouns under each card. Only the 1-vs-many
 * split is handled (the "N de facturi" form for ≥20 is not — the rest of the
 * app doesn't distinguish it either). */
function facturiEmise(count: number): string {
  return `${count} ${count === 1 ? 'factură emisă' : 'facturi emise'}`;
}
function platite(count: number): string {
  return `${count} ${count === 1 ? 'plătită' : 'plătite'}`;
}
function inAsteptare(count: number): string {
  return `${count} ${count === 1 ? 'factură în așteptare' : 'facturi în așteptare'}`;
}
function depasite(count: number): string {
  return `${count} ${count === 1 ? 'factură depășită' : 'facturi depășite'}`;
}

function SummaryCard({
  title,
  total,
  subtitle,
  color,
  loading,
}: {
  title: string;
  total: string;
  subtitle: string;
  color?: string;
  loading: boolean;
}) {
  return (
    <Card loading={loading}>
      <Statistic title={title} value={0} valueStyle={color ? { color } : undefined} formatter={() => <MoneyText value={total} />} />
      <Text type="secondary" style={{ fontSize: 12 }}>
        {subtitle}
      </Text>
    </Card>
  );
}

/**
 * The four payment-state stat cards above the Facturi list (facturat /
 * încasat / neîncasat / restante). Driven by `GET /invoices/summary`, so they
 * reflect the page's period + client + series + search scope but NOT its
 * status chips — the cards ARE the status breakdown. Încasat is green,
 * Restante red; the other two stay neutral (matching the reference design).
 */
export function FacturiSummaryCards({ data, loading }: { data: InvoiceSummary | undefined; loading: boolean }) {
  const {
    token: { colorSuccess, colorError },
  } = antTheme.useToken();

  const incasatSubtitle =
    data && data.incasat.rate !== null
      ? `${platite(data.incasat.count)} • ${data.incasat.rate}% rată încasare`
      : platite(data?.incasat.count ?? 0);

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      <Col xs={12} sm={12} xl={6}>
        <SummaryCard
          title="TOTAL FACTURAT"
          total={data?.facturat.total ?? '0.00'}
          subtitle={facturiEmise(data?.facturat.count ?? 0)}
          loading={loading}
        />
      </Col>
      <Col xs={12} sm={12} xl={6}>
        <SummaryCard
          title="ÎNCASAT"
          total={data?.incasat.total ?? '0.00'}
          subtitle={incasatSubtitle}
          color={colorSuccess}
          loading={loading}
        />
      </Col>
      <Col xs={12} sm={12} xl={6}>
        <SummaryCard
          title="NEÎNCASAT"
          total={data?.neincasat.total ?? '0.00'}
          subtitle={inAsteptare(data?.neincasat.count ?? 0)}
          loading={loading}
        />
      </Col>
      <Col xs={12} sm={12} xl={6}>
        <SummaryCard
          title="RESTANTE"
          total={data?.restante.total ?? '0.00'}
          subtitle={depasite(data?.restante.count ?? 0)}
          color={colorError}
          loading={loading}
        />
      </Col>
    </Row>
  );
}
